import React, { useState } from 'react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function DateEditor({ value, disabled, onAutoSave }) {
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDate = today.getDate();

  // Parse selected date to initialize view
  let initYear = todayYear;
  let initMonth = todayMonth;
  if (value) {
    const parts = value.split('-');
    if (parts.length === 3) {
      initYear = parseInt(parts[0], 10);
      initMonth = parseInt(parts[1], 10) - 1;
    }
  }

  const [viewYear, setViewYear] = useState(initYear);
  const [viewMonth, setViewMonth] = useState(initMonth);

  // Build calendar grid
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();

  const cells = [];
  // Previous month padding
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, current: false, offset: -1 });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, current: true, offset: 0 });
  }
  // Next month padding — fill to complete weeks
  const totalCells = cells.length <= 35 ? 35 : 42;
  for (let d = 1; cells.length < totalCells; d++) {
    cells.push({ day: d, current: false, offset: 1 });
  }

  const isToday = (cell) =>
    cell.current && cell.day === todayDate &&
    viewMonth === todayMonth && viewYear === todayYear;

  const isSelected = (cell) => {
    if (!value || !cell.current) return false;
    const parts = value.split('-');
    if (parts.length !== 3) return false;
    return (
      cell.day === parseInt(parts[2], 10) &&
      viewMonth === parseInt(parts[1], 10) - 1 &&
      viewYear === parseInt(parts[0], 10)
    );
  };

  const formatDate = (y, m, d) =>
    `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  // A greyed-out day belongs to the previous/next month: pick it AND turn
  // the calendar to that month, so the selection is visible.
  const handleDayClick = (cell) => {
    if (disabled) return;
    const d = new Date(viewYear, viewMonth + cell.offset, cell.day);
    if (cell.offset) { setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); }
    onAutoSave(formatDate(d.getFullYear(), d.getMonth(), d.getDate()));
  };

  const prevMonthNav = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonthNav = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  // « » step a whole year — a date years out (a warranty, a contract end)
  // was twelve clicks of ‹ › away.
  const prevYearNav = () => setViewYear(y => y - 1);
  const nextYearNav = () => setViewYear(y => y + 1);

  const handleToday = () => {
    if (disabled) return;
    onAutoSave(formatDate(todayYear, todayMonth, todayDate));
  };

  const handleClear = () => {
    if (disabled) return;
    // Send null, not '': Jira's date validator rejects an empty string
    // for custom date fields with "must be yyyy-MM-dd"; only null
    // actually clears the field.
    onAutoSave(null);
  };

  return (
    <div className="field-editor-calendar">
      <div className="field-editor-calendar-nav">
        <span className="field-editor-calendar-nav-group">
          <button
            type="button"
            className="field-editor-calendar-nav-btn field-editor-calendar-nav-btn--year"
            onClick={prevYearNav}
            disabled={disabled}
            title="Previous year"
            aria-label="Previous year"
          >
            &#171;
          </button>
          <button
            type="button"
            className="field-editor-calendar-nav-btn"
            onClick={prevMonthNav}
            disabled={disabled}
            title="Previous month"
            aria-label="Previous month"
          >
            &#8249;
          </button>
        </span>
        <span className="field-editor-calendar-title">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <span className="field-editor-calendar-nav-group">
          <button
            type="button"
            className="field-editor-calendar-nav-btn"
            onClick={nextMonthNav}
            disabled={disabled}
            title="Next month"
            aria-label="Next month"
          >
            &#8250;
          </button>
          <button
            type="button"
            className="field-editor-calendar-nav-btn field-editor-calendar-nav-btn--year"
            onClick={nextYearNav}
            disabled={disabled}
            title="Next year"
            aria-label="Next year"
          >
            &#187;
          </button>
        </span>
      </div>

      <div className="field-editor-calendar-grid">
        {DAY_NAMES.map(d => (
          <div key={d} className="field-editor-calendar-weekday">{d}</div>
        ))}
        {cells.map((cell, i) => (
          <div
            key={i}
            className={[
              'field-editor-calendar-day',
              !cell.current && 'other-month',
              isToday(cell) && 'today',
              isSelected(cell) && 'selected',
            ].filter(Boolean).join(' ')}
            onClick={() => handleDayClick(cell)}
          >
            <span className="field-editor-calendar-day-num">{cell.day}</span>
            {isToday(cell) && <span className="field-editor-calendar-today-dot" />}
          </div>
        ))}
      </div>

      <div className="field-editor-calendar-actions">
        <button
          type="button"
          className="field-editor-calendar-action-btn"
          onClick={handleToday}
          disabled={disabled}
        >
          Today
        </button>
        <button
          type="button"
          className="field-editor-calendar-action-btn field-editor-calendar-clear-btn"
          onClick={handleClear}
          disabled={disabled || !value}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

export default DateEditor;
